import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { UserCheck, UserX, Clock, AlertCircle } from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface PendingUser {
  user_id: string;
  full_name: string | null;
  specialty: string | null;
  created_at: string;
  email?: string;
}

interface UserApprovalQueueProps {
  onUserApproved?: () => void;
}

export function UserApprovalQueue({ onUserApproved }: UserApprovalQueueProps) {
  const [pendingUsers, setPendingUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<Record<string, string>>({});
  const [rejectionReason, setRejectionReason] = useState<Record<string, string>>({});
  const [showRejectInput, setShowRejectInput] = useState<Record<string, boolean>>({});

  const fetchPendingUsers = async () => {
    setLoading(true);
    try {
      // Query profiles first (actual schema: id, email, full_name)
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, email, full_name');

      const profilesMap = new Map(
        (profilesData || []).map(p => [p.id, p])
      );

      // Query pending accounts
      const { data, error } = await supabase
        .from('accounts')
        .select('user_id, created_at')
        .eq('status', 'pending_approval')
        .order('created_at', { ascending: true })
        .limit(10);

      if (error) throw error;

      const mapped = (data || []).map((acc: { user_id: string; created_at: string }) => {
        const profile = profilesMap.get(acc.user_id);
        return {
          user_id: acc.user_id,
          full_name: profile?.full_name || null,
          specialty: null, // profiles doesn't have specialty
          created_at: acc.created_at,
          email: profile?.email
        };
      });

      setPendingUsers(mapped);
    } catch (err) {
      console.error('Failed to fetch pending users:', err);
      toast.error('Failed to load pending approvals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const handleApprove = async (userId: string) => {
    setProcessingId(userId);
    try {
      const role = selectedRole[userId] || 'radiologist';
      const { data, error } = await supabase.rpc('admin_activate_user', {
        p_target_user_id: userId,
        p_assigned_role: role,
        p_reason: 'Approved via approval queue'
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table update
        if (error.code === '42883') {
          console.warn('admin_activate_user RPC not found - falling back to direct table update');

          // Update account status to active
          const { error: updateError } = await supabase
            .from('accounts')
            .update({ status: 'active' })
            .eq('user_id', userId);

          if (updateError) throw updateError;

          // Try to set user role
          try {
            const { data: existingRole } = await supabase
              .from('user_roles')
              .select('user_id')
              .eq('user_id', userId)
              .single();

            if (existingRole) {
              await supabase
                .from('user_roles')
                .update({ role })
                .eq('user_id', userId);
            } else {
              await supabase
                .from('user_roles')
                .insert({ user_id: userId, role });
            }
          } catch {
            console.warn('Could not set user role');
          }

          toast.success('User approved successfully');
          setPendingUsers(prev => prev.filter(u => u.user_id !== userId));
          onUserApproved?.();
          return;
        }
        throw error;
      }

      const result = data as { success: boolean; message?: string; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to approve user');
      }

      toast.success(result.message || 'User approved successfully');
      setPendingUsers(prev => prev.filter(u => u.user_id !== userId));
      onUserApproved?.();
    } catch (err) {
      console.error('Failed to approve user:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to approve user');
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async (userId: string) => {
    if (!showRejectInput[userId]) {
      setShowRejectInput(prev => ({ ...prev, [userId]: true }));
      return;
    }

    const reason = rejectionReason[userId];
    if (!reason?.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    setProcessingId(userId);
    try {
      const { data, error } = await supabase.rpc('admin_deactivate_user', {
        p_target_user_id: userId,
        p_reason: reason
      });

      if (error) {
        // If RPC doesn't exist, fall back to direct table update
        if (error.code === '42883') {
          console.warn('admin_deactivate_user RPC not found - falling back to direct table update');

          // Update account status to rejected
          const { error: updateError } = await supabase
            .from('accounts')
            .update({ status: 'rejected' })
            .eq('user_id', userId);

          if (updateError) throw updateError;

          toast.success('User rejected');
          setPendingUsers(prev => prev.filter(u => u.user_id !== userId));
          setShowRejectInput(prev => ({ ...prev, [userId]: false }));
          setRejectionReason(prev => ({ ...prev, [userId]: '' }));
          onUserApproved?.();
          return;
        }
        throw error;
      }

      const result = data as { success: boolean; message?: string; error?: string };
      if (!result.success) {
        throw new Error(result.error || 'Failed to reject user');
      }

      toast.success('User rejected');
      setPendingUsers(prev => prev.filter(u => u.user_id !== userId));
      setShowRejectInput(prev => ({ ...prev, [userId]: false }));
      setRejectionReason(prev => ({ ...prev, [userId]: '' }));
      onUserApproved?.();
    } catch (err) {
      console.error('Failed to reject user:', err);
      toast.error(err instanceof Error ? err.message : 'Failed to reject user');
    } finally {
      setProcessingId(null);
    }
  };

  const cancelReject = (userId: string) => {
    setShowRejectInput(prev => ({ ...prev, [userId]: false }));
    setRejectionReason(prev => ({ ...prev, [userId]: '' }));
  };

  if (loading) {
    return (
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Approval Queue
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, idx) => (
              <Skeleton key={idx} className="h-20 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (pendingUsers.length === 0) {
    return null; // Don't show the queue if empty
  }

  return (
    <Card className="mb-6 border-teal-200 bg-teal-50/50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-teal-700">
              <AlertCircle className="h-5 w-5" />
              Approval Queue
            </CardTitle>
            <CardDescription>
              {pendingUsers.length} user{pendingUsers.length === 1 ? '' : 's'} awaiting approval
            </CardDescription>
          </div>
          <Badge variant="secondary" className="bg-teal-100 text-teal-700">
            {pendingUsers.length} pending
          </Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {pendingUsers.map((user) => (
            <div
              key={user.user_id}
              className="bg-white rounded-lg p-4 border shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-medium truncate">
                      {user.full_name || 'Unnamed User'}
                    </span>
                    {user.specialty && (
                      <Badge variant="outline" className="text-xs">
                        {user.specialty}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Clock className="h-3 w-3" />
                    Requested {formatDistanceToNow(new Date(user.created_at), { addSuffix: true })}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <Select
                    value={selectedRole[user.user_id] || 'radiologist'}
                    onValueChange={(value) => setSelectedRole(prev => ({ ...prev, [user.user_id]: value }))}
                    disabled={processingId === user.user_id}
                  >
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="radiologist">Radiologist</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button
                    size="sm"
                    variant="default"
                    className="bg-green-600 hover:bg-green-700"
                    onClick={() => handleApprove(user.user_id)}
                    disabled={processingId === user.user_id}
                  >
                    <UserCheck className="h-4 w-4 mr-1" />
                    Approve
                  </Button>

                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleReject(user.user_id)}
                    disabled={processingId === user.user_id}
                  >
                    <UserX className="h-4 w-4 mr-1" />
                    Reject
                  </Button>
                </div>
              </div>

              {/* Rejection reason input */}
              {showRejectInput[user.user_id] && (
                <div className="mt-3 pt-3 border-t">
                  <label className="text-sm font-medium mb-1 block">
                    Rejection reason (required)
                  </label>
                  <div className="flex gap-2">
                    <Textarea
                      placeholder="Enter reason for rejection..."
                      value={rejectionReason[user.user_id] || ''}
                      onChange={(e) => setRejectionReason(prev => ({ ...prev, [user.user_id]: e.target.value }))}
                      className="min-h-[60px]"
                    />
                    <div className="flex flex-col gap-1">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleReject(user.user_id)}
                        disabled={processingId === user.user_id || !rejectionReason[user.user_id]?.trim()}
                      >
                        Confirm
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => cancelReject(user.user_id)}
                        disabled={processingId === user.user_id}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
